import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { useRecoilValue } from 'recoil';
import { ALIGNED_IMAGES_CHANGED } from '~/engine/data/Environment';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import type { Image3D } from '~/domain/patient/image3d';
import {
    describeSuvUnavailability,
    petImageOfPair,
} from '~/domain/patient/petImage';
import type { DatasetEnvironmentRegistry } from '~/engine/scene/DatasetEnvironmentRegistry';
import { sameElements } from '~/utils/array';
import { enqueueSnackbar } from '~/utils/notifications';
import { petImageOf } from '~/viewportGrid/adapters/engine';

type ResidentReader = Pick<
    DatasetEnvironmentRegistry,
    'environmentFor' | 'listEntries' | 'subscribeLifecycle'
>;

/** The last list read per registry, held while its membership is unchanged. */
const petSnapshots = new WeakMap<ResidentReader, readonly Image3D[]>();

/**
 * Every PET image a resident Environment draws, as its pair or aligned for
 * a row of its own, in registry order, each once. The same list comes back while the membership is unchanged, which
 * is what lets a store subscription settle and an effect key on the list.
 */
function readResidentPetImages(registry: ResidentReader): readonly Image3D[] {
    const images: Image3D[] = [];
    for (const entry of registry.listEntries()) {
        const environment = registry.environmentFor(entry.seriesUid);
        if (!environment) {
            continue;
        }
        const pets = [
            petImageOf(environment),
            ...environment
                .alignedVolumes()
                .map((volume) => petImageOfPair(volume, undefined)),
        ];
        for (const pet of pets) {
            if (pet && !images.includes(pet)) {
                images.push(pet);
            }
        }
    }
    const previous = petSnapshots.get(registry);
    if (previous && sameElements(previous, images)) {
        return previous;
    }
    petSnapshots.set(registry, images);
    return images;
}

/**
 * Warns once per PET image a resident row shows, fused, aligned or as its
 * own image, whose values cannot be
 * read as SUVbw, so the activity or counts fallback is never silent. A
 * series drawn on two rows counts once, and an image warned about is not
 * warned about again, whatever row shows it next.
 */
export function usePetSuvAvailabilityWarning(): void {
    const registry = getDatasetEnvironmentRegistry();
    const viewManager = useRecoilValue(viewManagerState);
    // An aligned image is held after its row's Environment is acquired, and
    // let go before it is released, so its own announcement is followed too.
    const subscribe = useCallback(
        (listener: () => void) => {
            const stopLifecycle = registry.subscribeLifecycle(listener);
            viewManager?.addEventListener(ALIGNED_IMAGES_CHANGED, listener);
            return () => {
                stopLifecycle();
                viewManager?.removeEventListener(
                    ALIGNED_IMAGES_CHANGED,
                    listener
                );
            };
        },
        [registry, viewManager]
    );
    const read = useCallback(() => readResidentPetImages(registry), [registry]);
    const petImages = useSyncExternalStore(subscribe, read);

    const warned = useRef(new WeakSet<Image3D>());
    useEffect(() => {
        for (const pet of petImages) {
            if (warned.current.has(pet) || pet.suvFactor !== null) {
                continue;
            }
            warned.current.add(pet);
            enqueueSnackbar(describeSuvUnavailability(pet.suvQuantification), {
                variant: 'warning',
                preventDuplicate: true,
            });
        }
    }, [petImages]);
}
